import { useCallback, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Table,
  Tag,
  Button,
  Drawer,
  message,
  Progress,
  Tooltip,
  Collapse,
  Popconfirm,
  Input,
} from "antd";
import {
  InfoCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_COLOR = {
  not_started: "gray",
  pending: "orange",
  in_progress: "blue",
  completed: "green",
  failed: "red",
};

const OnboardingPage = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [deletingRows, setDeletingRows] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchDeleting, setBatchDeleting] = useState(false);
  const [searchText, setSearchText] = useState("");

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/onboarding/status-all`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = await res.json();
      // detect duplicate onboarding_id values and duplicate emails
      const idCounts = payload.reduce((acc, it) => {
        if (!it || !it.onboarding_id) return acc;
        acc[it.onboarding_id] = (acc[it.onboarding_id] || 0) + 1;
        return acc;
      }, {});

      const emailCounts = payload.reduce((acc, it) => {
        if (!it || !it.email) return acc;
        const norm = String(it.email).trim().toLowerCase();
        if (!norm) return acc;
        acc[norm] = (acc[norm] || 0) + 1;
        return acc;
      }, {});

      const formatted = payload.map((item, idx) => {
        const uidKey = `${item.onboarding_id || "noid"}-${idx}`;
        const normEmail =
          item && item.email ? String(item.email).trim().toLowerCase() : "";
        const dupById =
          item && item.onboarding_id ? idCounts[item.onboarding_id] > 1 : false;
        const dupByEmail = normEmail ? emailCounts[normEmail] > 1 : false;
        let duplicateReason = null;
        if (dupById && dupByEmail) duplicateReason = "both";
        else if (dupById) duplicateReason = "onboarding_id";
        else if (dupByEmail) duplicateReason = "email";

        return {
          key: uidKey,
          uid: uidKey,
          onboarding_id: item.onboarding_id,
          email: item.email,
          website: item.website,
          status: item.status || {},
          raw: item,
          isDuplicate: !!duplicateReason,
          duplicateReason,
        };
      });
      setData(formatted);
      setSelectedRowKeys([]);
    } catch (err) {
      console.error(err);
      message.error(
        t(
          "superAdmin.onboarding.failedToLoad",
          "Failed to load onboarding list",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const showDetails = (record) => {
    setDetailRecord(record);
    setDetailVisible(true);
  };

  const deleteOnboarding = async (uid, onboarding_id) => {
    console.debug("deleteOnboarding called", { uid, onboarding_id });
    setDeletingRows((prev) => ({ ...prev, [uid]: true }));
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/v1/onboarding/${onboarding_id}`,
        { method: "DELETE", credentials: "include" },
      );
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.message || "Delete failed");
      message.success(
        payload.message || t("superAdmin.onboarding.deleted", "Deleted"),
      );
      await fetchList();
    } catch (err) {
      console.error(err);
      message.error(
        t(
          "superAdmin.onboarding.failedToDelete",
          "Failed to delete onboarding",
        ),
      );
    } finally {
      setDeletingRows((prev) => ({ ...prev, [uid]: false }));
    }
  };

  const handleBatchDelete = async () => {
    setBatchDeleting(true);
    let successCount = 0;
    let errorCount = 0;

    const selectedRecords = data.filter((item) =>
      selectedRowKeys.includes(item.uid),
    );

    const recordsByOnboardingId = selectedRecords.reduce((acc, record) => {
      const onboardingId = record.onboarding_id;
      if (
        onboardingId === undefined ||
        onboardingId === null ||
        onboardingId === ""
      ) {
        errorCount++;
        return acc;
      }
      if (!acc[onboardingId]) {
        acc[onboardingId] = [];
      }
      acc[onboardingId].push(record);
      return acc;
    }, {});

    const deletePromises = Object.entries(recordsByOnboardingId).map(
      async ([onboardingId, records]) => {
        records.forEach((record) => {
          setDeletingRows((prev) => ({ ...prev, [record.uid]: true }));
        });
        try {
          const res = await fetch(
            `${BACKEND_URL}/api/v1/onboarding/${onboardingId}`,
            { method: "DELETE", credentials: "include" },
          );
          if (!res.ok) {
            const payload = await res.json().catch(() => ({}));
            throw new Error(payload.message || "Delete failed");
          }
          successCount++;
        } catch (err) {
          console.error(err);
          errorCount++;
        } finally {
          records.forEach((record) => {
            setDeletingRows((prev) => ({ ...prev, [record.uid]: false }));
          });
        }
      },
    );

    await Promise.all(deletePromises);

    if (successCount > 0)
      message.success(
        t("superAdmin.onboarding.deletedCount", {
          count: successCount,
          defaultValue: `Deleted ${successCount} onboarding(s)`,
        }),
      );
    if (errorCount > 0)
      message.error(
        t("superAdmin.onboarding.failedDeleteCount", {
          count: errorCount,
          defaultValue: `Failed to delete ${errorCount} onboarding(s)`,
        }),
      );

    setSelectedRowKeys([]);
    setBatchDeleting(false);
    await fetchList();
  };

  const filteredData = useMemo(() => {
    if (!searchText) return data;
    const lowerSearch = searchText.toLowerCase();

    return data.filter((item) => {
      const parts = [];
      if (item.uid) parts.push(String(item.uid));
      if (item.onboarding_id || item.onboarding_id === 0)
        parts.push(String(item.onboarding_id));
      if (item.email) parts.push(String(item.email));
      if (item.website) parts.push(String(item.website));

      if (item.status) {
        try {
          parts.push(JSON.stringify(item.status));
        } catch (e) {}
      }
      if (item.raw) {
        try {
          parts.push(JSON.stringify(item.raw));
        } catch (e) {}
      }

      const hay = parts.filter(Boolean).join(" ").toLowerCase();
      return hay.includes(lowerSearch);
    });
  }, [data, searchText]);

  // Using inline Popconfirm for delete to keep UX simple and avoid z-index/overlay issues

  const renderSubStatus = (obj) => {
    if (!obj) return "-";
    const name = obj?.state || obj?.phase || obj?.status || "unknown";
    const color = STATUS_COLOR[name] || "gray";
    const progress = obj?.progress ?? obj?.percent ?? obj?.percentage;
    const messageText = obj?.message || obj?.error_message || obj?.last_message;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Tag color={color} style={{ textTransform: "capitalize" }}>
            {name}
          </Tag>
          {messageText ? (
            <Tooltip title={messageText}>
              <InfoCircleOutlined />
            </Tooltip>
          ) : null}
        </div>
        {typeof progress === "number" ? (
          <Progress
            percent={Math.max(0, Math.min(100, Math.round(progress)))}
            size="small"
          />
        ) : null}
      </div>
    );
  };

  const renderKeyValues = (obj) => {
    if (!obj || typeof obj !== "object")
      return <div style={{ color: "#888" }}>No data</div>;

    // show state prominently if present
    const state = obj.state ?? obj.phase ?? obj.status;
    const entries = Object.entries(obj).filter(
      ([k, v]) => v !== null && v !== undefined && k !== "state",
    );

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {state ? (
          <div style={{ marginBottom: 6 }}>
            <Tag color={STATUS_COLOR[state] || "gray"}>{String(state)}</Tag>
          </div>
        ) : null}
        {entries.length === 0 ? (
          <div style={{ color: "#888" }}>No additional details</div>
        ) : (
          entries.map(([k, v]) => (
            <div key={k} style={{ display: "flex", gap: 12 }}>
              <strong style={{ width: 160, textTransform: "capitalize" }}>
                {k}
              </strong>
              <div>
                {typeof v === "object" ? (
                  <pre style={{ margin: 0 }}>{JSON.stringify(v, null, 2)}</pre>
                ) : (
                  String(v)
                )}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  const columns = [
    {
      title: t("superAdmin.onboarding.duplicate", "Duplicate"),
      dataIndex: "isDuplicate",
      key: "duplicate",
      render: (_, record) => {
        if (!record?.isDuplicate) return null;
        const reason = record.duplicateReason || "duplicate";
        const label =
          reason === "both"
            ? t(
                "superAdmin.onboarding.duplicateIdAndEmail",
                "Duplicate (id & email)",
              )
            : reason === "email"
              ? t("superAdmin.onboarding.duplicateEmail", "Duplicate (email)")
              : t("superAdmin.onboarding.duplicateId", "Duplicate (id)");
        return (
          <Tooltip title={label}>
            <Tag color="red">{label}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: t("superAdmin.onboarding.email", "Email"),
      dataIndex: "email",
      key: "email",
      render: (text) => <a href={`mailto:${text}`}>{text}</a>,
    },
    {
      title: t("superAdmin.onboarding.website", "Website"),
      dataIndex: "website",
      key: "website",
      render: (text) =>
        text ? (
          <a href={text} target="_blank" rel="noreferrer">
            {text}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: t("superAdmin.onboarding.overall", "Overall"),
      dataIndex: ["status", "overall"],
      key: "overall",
      render: (overall) => renderSubStatus(overall),
    },
    {
      title: t("superAdmin.onboarding.dbSetup", "DB Setup"),
      dataIndex: ["status", "db_setup"],
      key: "db_setup",
      render: (db) => renderSubStatus(db),
    },
    {
      title: t("superAdmin.onboarding.contentIngestion", "Content Ingestion"),
      dataIndex: ["status", "content_ingestion"],
      key: "content_ingestion",
      render: (ci) => renderSubStatus(ci),
    },
    {
      title: t("superAdmin.onboarding.promptProcessing", "Prompt Processing"),
      dataIndex: ["status", "query_processing"],
      key: "query_processing",
      render: (qp) => renderSubStatus(qp),
    },
    {
      title: t("superAdmin.onboarding.details", "Details"),
      key: "details",
      render: (_, record) => (
        <Button type="link" onClick={() => showDetails(record)}>
          {t("superAdmin.onboarding.details", "Details")}
        </Button>
      ),
    },
    {
      title: t("superAdmin.onboarding.delete", "Delete"),
      key: "delete",
      render: (_, record) => {
        const isDeleting = !!deletingRows[record.uid];
        return (
          <Popconfirm
            title={t(
              "superAdmin.onboarding.deleteConfirm",
              `Delete onboarding ${record.onboarding_id}?`,
              { id: record.onboarding_id },
            )}
            onConfirm={() => deleteOnboarding(record.uid, record.onboarding_id)}
            okText={t("superAdmin.onboarding.delete", "Delete")}
            okButtonProps={{ danger: true }}
            cancelText={t("superAdmin.onboarding.cancel", "Cancel")}
          >
            <Button type="link" danger loading={isDeleting}>
              {!isDeleting && (
                <span>{t("superAdmin.onboarding.delete", "Delete")}</span>
              )}
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <h2 style={{ margin: 0 }}>
            {t("superAdmin.onboarding.title", "Onboarding Management")}
          </h2>
          <Input
            placeholder={t(
              "superAdmin.onboarding.searchPlaceholder",
              "Search by any field...",
            )}
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {selectedRowKeys.length > 0 && (
            <Popconfirm
              title={t(
                "superAdmin.onboarding.deleteBatchConfirm",
                `Delete ${selectedRowKeys.length} onboarding(s)?`,
                { count: selectedRowKeys.length },
              )}
              onConfirm={handleBatchDelete}
              okText={t("superAdmin.onboarding.deleteAll", "Delete All")}
              okButtonProps={{ danger: true }}
              cancelText={t("superAdmin.onboarding.cancel", "Cancel")}
            >
              <Button type="primary" danger loading={batchDeleting}>
                {t("superAdmin.onboarding.deleteSelected", "Delete Selected")} (
                {selectedRowKeys.length})
              </Button>
            </Popconfirm>
          )}
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchList}
            loading={loading}
          >
            {t("superAdmin.onboarding.refresh", "Refresh")}
          </Button>
        </div>
      </div>

      <div style={{ width: "100%" }}>
        {loading ? (
          <>
            <style>{`@keyframes dotMove {0%{opacity:0.25; transform:translateY(0)}50%{opacity:1; transform:translateY(-8px)}100%{opacity:0.25; transform:translateY(0)}}`}</style>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 300,
                flexDirection: "column",
              }}
            >
              <div style={{ display: "flex", gap: 10 }}>
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: "#1890ff",
                      animation: `dotMove 1s ${i * 0.12}s infinite ease-in-out`,
                    }}
                  />
                ))}
              </div>
              <div style={{ marginTop: 12, color: "#888" }}>
                {t(
                  "superAdmin.onboarding.loadingOnboarding",
                  "Loading onboarding…",
                )}
              </div>
            </div>
          </>
        ) : (
          <Table
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
            }}
            columns={columns}
            dataSource={filteredData}
            pagination={{ pageSize: 20 }}
            rowKey="uid"
          />
        )}
      </div>

      <Drawer
        title={`Onboarding ${detailRecord?.onboarding_id}`}
        placement="right"
        width={640}
        onClose={() => setDetailVisible(false)}
        open={detailVisible}
      >
        {detailRecord?.raw ? (
          <Collapse defaultActiveKey={["overall"]} ghost>
            <Collapse.Panel header="Overall" key="overall">
              {renderKeyValues(detailRecord.raw.status?.overall)}
            </Collapse.Panel>
            <Collapse.Panel header="DB Setup" key="db_setup">
              {renderKeyValues(detailRecord.raw.status?.db_setup)}
            </Collapse.Panel>
            <Collapse.Panel header="Content Ingestion" key="content_ingestion">
              {renderKeyValues(detailRecord.raw.status?.content_ingestion)}
            </Collapse.Panel>
            <Collapse.Panel header="Prompt Processing" key="query_processing">
              {renderKeyValues(detailRecord.raw.status?.query_processing)}
            </Collapse.Panel>
            <Collapse.Panel header="Raw JSON" key="raw">
              <pre style={{ whiteSpace: "pre-wrap" }}>
                {JSON.stringify(detailRecord.raw, null, 2)}
              </pre>
            </Collapse.Panel>
          </Collapse>
        ) : null}
      </Drawer>
    </div>
  );
};

export default OnboardingPage;
